/**
 * CreditNoteFormDialog — New / Edit for Sales Control › Credit Note.
 * Converted 1:1 from Access `frmCtrlSalesQryCreditAdd` (NO Copy mode). 2-col form
 * (Access Left 2592 → col 1, Left 9612 → col 2) ordered by Top.
 *
 * Left col:  ID(ro) · Credit No.(index) · Credit Code(gen) · Credit Date ·
 *            Customer's Debit No · Payment Date · Note
 * Right col: Claim ID(combo) · Client(ro) · Claim Type(ro) · Claim Value(ro) ·
 *            Claim Currency(ro) · Invoice No(ro) · Credited Amount · Price Unit(combo) · FX
 *
 * Cascade (Access claim_credit_no_index_AfterUpdate + claim_scs_code_AfterUpdate):
 *   both rebuild credit_no = "CN" + zeroPad(index,4) + "/" + invoiceNo
 *   (invoiceNo = customer_invoice_no from claim snapshot). After rebuild, check
 *   duplicate credit_no → if exists warn "Credit Code is existed!" and clear.
 *
 * Required (strCheck): claim_credit_no_index, claim_scs_code, credit_no, credit_date.
 * Edit disables claim_credit_no_index + claim_scs_code (Access .Enabled=False).
 * No Copy mode.
 */
import { useEffect, useRef, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type CnLookupItem,
  type SaleCreditNoteWrite,
  useCreateSaleCreditNoteMutation,
  useUpdateSaleCreditNoteMutation,
  useLazyGetSaleCreditNoteFormQuery,
  useLazyGetCreditClaimSnapshotQuery,
  useLazySuggestCreditNoQuery,
  useLazyCheckCreditNoQuery,
  useCnLookupClaimCodesQuery,
  useCnLookupPriceUnitsQuery,
} from '../../store/api/saleCreditNoteApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  creditNoteId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  claimScsCode: string
  claimCreditNoIndex: string
  creditNo: string
  creditDate: string
  creditAmount: string
  customerDebitNo: string
  priceUnit: string
  paymentDate: string
  fx: string
  note: string
  // readonly display (claim snapshot + row id)
  claimCreditScsId: string
  client: string
  claimType: string
  claimValue: string
  claimCurrency: string
  invoiceNo: string
}

const EMPTY: Draft = {
  claimScsCode: '', claimCreditNoIndex: '', creditNo: '', creditDate: '', creditAmount: '',
  customerDebitNo: '', priceUnit: '', paymentDate: '', fx: '', note: '',
  claimCreditScsId: '', client: '', claimType: '', claimValue: '', claimCurrency: '', invoiceNo: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const gridSx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

function buildCreditNo(index: string, invoiceNo: string): string {
  const idx = index.trim()
  if (!idx) return ''
  const n = parseInt(idx, 10)
  if (!Number.isFinite(n)) return ''
  return `CN${String(n).padStart(4, '0')}/${invoiceNo}`
}

function Combo(props: {
  label: string
  value: string
  options: CnLookupItem[]
  loading?: boolean
  required?: boolean
  disabled?: boolean
  error?: boolean
  tabIndex?: number
  onPick: (value: string) => void
}) {
  const { label, value, options, loading, required, disabled, error, tabIndex, onPick } = props
  const sel = value ? options.find((o) => o.value === value) ?? { value, label: value } : null
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size={SM}
      fullWidth
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '')}
      getOptionLabel={(o) => (!o ? '' : o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          error={error}
          inputProps={{ ...params.inputProps, tabIndex }}
        />
      )}
    />
  )
}

export default function CreditNoteFormDialog({ open, mode, creditNoteId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  const [loadForm] = useLazyGetSaleCreditNoteFormQuery()
  const [loadSnapshot] = useLazyGetCreditClaimSnapshotQuery()
  const [suggest] = useLazySuggestCreditNoQuery()
  const [checkCredit] = useLazyCheckCreditNoQuery()
  const [createCredit, { isLoading: creating }] = useCreateSaleCreditNoteMutation()
  const [updateCredit, { isLoading: updating }] = useUpdateSaleCreditNoteMutation()
  const saving = creating || updating

  const skip = { skip: !open }
  const claimCodes = useCnLookupClaimCodesQuery(undefined, skip)
  const priceUnits = useCnLookupPriceUnitsQuery(undefined, skip)

  const readonly = mode === 'edit'

  // ── rebuild credit_no + duplicate check (both AfterUpdate cascades) ─────────
  const rebuildAndCheck = async (index: string, invoiceNo: string) => {
    const cn = buildCreditNo(index, invoiceNo)
    setDraft((p) => ({ ...p, creditNo: cn }))
    if (!cn || readonly) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await checkCredit({
        creditNo: cn,
        excludeId: undefined,
      }).unwrap()
      if (check.exists) {
        setDraft((p) => ({ ...p, creditNo: '' }))
        snack.warning('Credit Code is existed!')
      }
    } catch {
      // BE re-checks on save; do not poison UX.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  // ── claim snapshot → readonly display + invoiceNo (returns invoiceNo) ───────
  const applySnapshot = async (claimScsCode: string): Promise<string> => {
    if (!claimScsCode) {
      setDraft((p) => ({ ...p, client: '', claimType: '', claimValue: '', claimCurrency: '', invoiceNo: '' }))
      return ''
    }
    const snap = await loadSnapshot(claimScsCode).unwrap().catch(() => null)
    const invoiceNo = snap?.invoiceNo ?? ''
    setDraft((p) => ({
      ...p,
      client: snap?.client ?? '',
      claimType: snap?.claimType ?? '',
      claimValue: fmtStd(snap?.claimValue),
      claimCurrency: snap?.claimCurrency ?? '',
      invoiceNo,
    }))
    return invoiceNo
  }

  // ── Hydrate on open (deps: [open, mode, creditNoteId] only) ─────────────────
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    void (async () => {
      if (mode === 'new') {
        const idx = await suggest().unwrap().catch(() => null)
        if (cancelled) return
        setDraft({ ...EMPTY, claimCreditNoIndex: idx ? String(idx.index) : '' })
        return
      }
      if (!creditNoteId) return
      const f = await loadForm(creditNoteId).unwrap().catch(() => null)
      if (cancelled || !f) return
      const g = (k: string) => s((f as Record<string, unknown>)[k])
      const creditNo = g('creditNo')
      // Edit: derive index from creditNo = "CN0001/<invoice>" → 1
      const index = creditNo.split('/')[0].replace(/^CN/i, '').replace(/^0+/, '')
      const claimScsCode = g('claimScsCode')
      setDraft({
        ...EMPTY,
        claimScsCode,
        claimCreditNoIndex: index,
        creditNo,
        creditDate: g('creditDate').slice(0, 10),
        creditAmount: fmtStd(g('creditAmount')),
        customerDebitNo: g('customerDebitNo'),
        priceUnit: g('priceUnit'),
        paymentDate: g('paymentDate').slice(0, 10),
        fx: fmtStd(g('fx')),
        note: g('note'),
        claimCreditScsId: String(creditNoteId),
      })
      // Fill readonly display from the claim snapshot.
      void applySnapshot(claimScsCode)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, creditNoteId])

  // ── Cascades ────────────────────────────────────────────────────────────────
  const onIndexChange = (index: string) => {
    setDraft((p) => ({ ...p, claimCreditNoIndex: index }))
    void rebuildAndCheck(index, draft.invoiceNo)
  }
  const onClaimPick = (claimScsCode: string) => {
    setDraft((p) => ({ ...p, claimScsCode }))
    void (async () => {
      const invoiceNo = await applySnapshot(claimScsCode)
      await rebuildAndCheck(draft.claimCreditNoIndex, invoiceNo)
    })()
  }

  const indexEmpty = !draft.claimCreditNoIndex.trim()
  const claimEmpty = !draft.claimScsCode.trim()
  const creditNoEmpty = !draft.creditNo.trim()
  const dateEmpty = !draft.creditDate.trim()
  const canSave = canEdit && !indexEmpty && !claimEmpty && !creditNoEmpty && !dateEmpty && !saving && !precheckBusy

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (precheckBusyRef.current) return
    if (!canSave) return
    const payload: SaleCreditNoteWrite = {
      claimScsCode: draft.claimScsCode || null,
      creditNo: draft.creditNo || null,
      customerDebitNo: draft.customerDebitNo || null,
      creditDate: draft.creditDate || null,
      creditAmount: parseNum(draft.creditAmount),
      note: draft.note || null,
      priceUnit: draft.priceUnit || null,
      paymentDate: draft.paymentDate || null,
      fx: parseNum(draft.fx),
    }
    try {
      if (mode === 'edit' && creditNoteId) {
        await updateCredit({ id: creditNoteId, body: payload }).unwrap()
        snack.success('Successfully Updated')
      } else {
        await createCredit(payload).unwrap()
        snack.success('Successfully Added')
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Save credit note failed'))
    }
  }

  const title = mode === 'new' ? 'New' : 'Edit'

  const dateField = (key: keyof Draft, label: string, required?: boolean, tabIndex?: number) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{
        textField: {
          size: SM, fullWidth: true, required,
          error: required && !(draft[key] as string),
          InputLabelProps: { shrink: true },
          inputProps: { tabIndex },
        },
      }}
    />
  )
  const numField = (key: keyof Draft, label: string, tabIndex?: number) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => set(key, fmtStd(draft[key] as string) as Draft[typeof key])}
      inputProps={{ ...numProps, tabIndex }}
    />
  )
  const textField = (key: keyof Draft, label: string, tabIndex?: number, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      inputProps={{ tabIndex }} {...extra} />
  )
  const roField = (key: keyof Draft, label: string, numeric?: boolean) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
      inputProps={{ tabIndex: -1, ...(numeric ? { style: { textAlign: 'right' } } : {}) }} />
  )
  const cell = (col: number, row: number | string, node: React.ReactNode) => (
    <Box sx={{ gridColumn: { sm: col }, gridRow: { sm: row } }}>{node}</Box>
  )

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Sale Control - Credit Note — {title}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit credit notes.</Alert>}
        <Box sx={gridSx}>
          {/* ── Left column (Access Left=2592) ── */}
          {cell(1, 1, roField('claimCreditScsId', 'ID'))}
          {cell(1, 2, (
            <TextField
              label="Credit No. *" size={SM} fullWidth disabled={readonly}
              value={draft.claimCreditNoIndex}
              onChange={(e) => onIndexChange(e.target.value)}
              error={indexEmpty} inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric', tabIndex: 1 }}
              helperText={readonly ? 'Credit No. cannot be changed after creation.' : undefined}
            />
          ))}
          {cell(1, 3, (
            <TextField
              label="Credit Code *" size={SM} fullWidth value={draft.creditNo}
              InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
              error={creditNoEmpty}
              inputProps={{ tabIndex: -1 }}
              helperText={precheckBusy ? 'Verifying Credit Code…' : 'Generated from Credit No. and Claim ID.'}
            />
          ))}
          {cell(1, 4, dateField('creditDate', 'Credit Date', true, 4))}
          {cell(1, 5, textField('customerDebitNo', "Customer's Debit No", 5))}
          {cell(1, 6, dateField('paymentDate', 'Payment Date', false, 8))}
          {cell(1, '7 / span 3', textField('note', 'Note', 10, { multiline: true, minRows: 3 }))}

          {/* ── Right column (Access Left=9612) ── */}
          {cell(2, 1, (
            <Combo label="Claim ID" value={draft.claimScsCode} required disabled={readonly}
              error={claimEmpty} options={claimCodes.data?.items ?? []} loading={claimCodes.isFetching}
              tabIndex={2} onPick={onClaimPick} />
          ))}
          {cell(2, 2, roField('client', 'Client/Buyer Name'))}
          {cell(2, 3, roField('claimType', 'Claim Type'))}
          {cell(2, 4, roField('claimValue', 'Claim Value', true))}
          {cell(2, 5, roField('claimCurrency', 'Claim Currency'))}
          {cell(2, 6, roField('invoiceNo', 'Invoice No'))}
          {cell(2, 7, numField('creditAmount', 'Credited Amount', 6))}
          {cell(2, 8, (
            <Combo label="Price Unit" value={draft.priceUnit} options={priceUnits.data?.items ?? []}
              loading={priceUnits.isFetching} tabIndex={7} onPick={(v) => set('priceUnit', v)} />
          ))}
          {cell(2, 9, numField('fx', 'FX', 9))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
